import { Injectable } from '@angular/core';
import { Project } from '@src/app/models/project';
import { Person } from '@src/app/models/person';
import { Task } from '@src/app/models/task';
import { ProjectsPerMonthData } from '../model/projects-per-month';
import { ExecutionSpeedData } from '../model/execution-speed-data';
import { WeeklyCapacityData } from '../model/weekly-capacity-data';

/**
 * Consultant interface for calculation service
 * Supports both Person and PersonOrganization types
 */
interface Consultant {
  fte?: number;
  isActive?: boolean;
  personId?: string;
}

/**
 * ProjectDash Calculation Service
 * 
 * Provides calculations for dashboard metrics:
 * - Projects Per Month: 6-month completion trend
 * - Execution Speed: 24-hour team velocity percentage
 * - Weekly Capacity: Current week capacity vs planned work
 * 
 * All calculations handle edge cases (empty data, null values, zero divisions)
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectdashCalculationService {

  /**
   * Calculate projects active per month for the last 6 months
   * 
   * A project is counted as active in a month if:
   * - start_date is on or before the last day of the month
   * - end_date is on or after the first day of the month
   * 
   * @param projects Array of projects with start_date and end_date
   * @returns Array of monthly data sorted chronologically (oldest to newest)
   */
  calculateProjectsPerMonth(projects: Project[]): ProjectsPerMonthData[] {
    const months = this.getLast6Months();
    
    return months.map(month => {
      const monthStart = new Date(Date.UTC(month.getFullYear(), month.getMonth(), 1));
      const monthEnd = new Date(Date.UTC(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59));
      
      const count = projects.filter(p => {
        if (!p.start_date || !p.end_date) {
          return false;
        }
        
        const projectStart = new Date(p.start_date);
        const projectEnd = new Date(p.end_date);
        
        // Project is active in this month if it overlaps with the month period
        return projectStart <= monthEnd && projectEnd >= monthStart;
      }).length;
      
      return {
        month: this.formatMonth(month),
        count,
        monthDate: month
      };
    });
  }

  /**
   * Calculate team execution speed (24-hour velocity)
   * 
   * Formula: (Σ completed_workload_24h / Σ consultant_fte) × 100
   * 
   * Only includes:
   * - Tasks completed in last 24 hours with taskstate='Done'
   * - Active consultants (isActive=true)
   * - Projects with valid workload_fte
   * 
   * @param projects Array of active projects
   * @param consultants Array of team members (Person or PersonOrganization)
   * @param tasks Array of all tasks with completed_date set
   * @returns ExecutionSpeedData with calculated percentage and supporting metrics
   */
  calculateExecutionSpeed(
    projects: Project[], 
    consultants: Consultant[],
    tasks: Task[]
  ): ExecutionSpeedData {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    // Filter completed tasks in last 24h
    const recentlyCompleted = tasks.filter(t => 
      t.taskstate === 'Done' &&
      t.completed_date &&
      new Date(t.completed_date) >= yesterday &&
      new Date(t.completed_date) <= now
    );
    
    // Calculate completed workload per project
    let completedWorkload = 0;
    projects.forEach(project => {
      const projectTasks = tasks.filter(t => t.projectId === project.projectId);
      const completedInProject = recentlyCompleted.filter(t => t.projectId === project.projectId);
      
      if (projectTasks.length > 0) {
        const workloadFte = project.workload_fte || 1.0;
        completedWorkload += (completedInProject.length / projectTasks.length) * workloadFte;
      }
    });
    
    // Calculate total capacity (active consultants only)
    const totalCapacity = consultants
      .filter(c => c.isActive)
      .reduce((sum, c) => sum + (c.fte || 1.0), 0);
    
    const speedPercent = totalCapacity > 0 
      ? Math.round((completedWorkload / totalCapacity) * 100)
      : 0;
    
    return {
      speedPercent,
      completedWorkload,
      totalCapacity,
      completedTasksCount: recentlyCompleted.length,
      totalTasksCount: tasks.length
    };
  }

  /**
   * Calculate weekly capacity vs planned work for 5 weeks ahead
   * 
   * Capacity = Σ consultant_fte (constant per week)
   * Planned Work = Σ workload_fte for active projects overlapping each week (Mon-Fri)
   * 
   * @param projects Array of active projects with date range
   * @param consultants Array of team members (Person or PersonOrganization)
   * @returns Array of weekly capacity data for 5 consecutive weeks
   */
  calculateWeeklyCapacity(
    projects: Project[],
    consultants: Consultant[]
  ): WeeklyCapacityData[] {
    const weekStarts = this.getNext5WeekStarts();
    
    // Calculate total capacity (constant across weeks)
    const weeklyCapacity = consultants
      .filter(c => c.isActive)
      .reduce((sum, c) => sum + (c.fte || 1.0), 0);
    
    return weekStarts.map((weekStart, weekIndex) => {
      // Calculate planned work as sum of workload_fte for active projects overlapping this week
      let plannedWork = 0;

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 4); // Monday + 4 = Friday
      weekEnd.setHours(23, 59, 59, 999);

      // Include projects that are active on this specific day
      projects.forEach(project => {
        if (project.isactive !== true || !project.end_date) {
          return;
        }

        // Parse project dates (missing start_date means project is already started)
        let projectStart: Date | null = null;
        if (project.start_date) {
          projectStart = new Date(project.start_date);
          projectStart.setHours(0, 0, 0, 0);
        }
        
        const projectEnd = new Date(project.end_date);
        projectEnd.setHours(23, 59, 59, 999);
        
        // Check overlap with week window: projectStart <= weekEnd AND projectEnd >= weekStart
        if ((!projectStart || projectStart <= weekEnd) && projectEnd >= weekStart) {
          plannedWork += (project.workload_fte || 1.0);
        }
      });
      
      return {
        weekday: `Week ${weekIndex + 1}`,
        capacity: weeklyCapacity,
        plannedWork: Math.round(plannedWork * 100) / 100, // Round to 2 decimals
        date: weekStart
      };
    });
  }

  // ==================== Utility Methods ====================

  /**
   * Get array of last 6 months, each as the first day of the month
   */
  private getLast6Months(): Date[] {
    const months: Date[] = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1)));
    }
    
    return months;
  }

  /**
   * Get array of Monday dates for current week + next 4 weeks
   */
  private getNext5WeekStarts(): Date[] {
    const today = new Date();
    const dayOfWeek = today.getDay();
    
    // Calculate Monday of work week (on Sunday, use upcoming Monday)
    const diff = today.getDate() - dayOfWeek + 1;
    const monday = new Date(today.setDate(diff));
    
    const weekStarts: Date[] = [];
    for (let i = 0; i < 5; i++) {
      const weekStart = new Date(monday);
      weekStart.setDate(monday.getDate() + (i * 7));
      weekStart.setHours(0, 0, 0, 0);
      weekStarts.push(weekStart);
    }
    
    return weekStarts;
  }

  /**
   * Get remaining business days in the current work week from a given date
   */
  private getRemainingDaysInWeek(fromDate: Date): number {
    const dayOfWeek = fromDate.getDay();
    // 1 = Monday, 5 = Friday
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return 5 - daysFromMonday;
  }

  /**
   * Calculate business days between two dates (Mon-Fri only)
   * Includes the toDate if it's a business day
   */
  private getBusinessDaysUntil(fromDate: Date, toDate: Date): number {
    // Ensure both are Date objects
    const from = new Date(fromDate);
    const to = new Date(toDate);
    
    if (!to || to < from) {
      return 0;
    }
    
    let businessDays = 0;
    const current = new Date(from);
    
    // Reset time to start of day for consistent comparison
    current.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);
    
    // Include toDate in the calculation (<=, not <)
    while (current <= to) {
      const dayOfWeek = current.getDay();
      // Include Mon-Fri (1-5)
      if (dayOfWeek > 0 && dayOfWeek < 6) {
        businessDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return businessDays;
  }

  /**
   * Check if two dates fall in the same month
   */
  private isSameMonth(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth();
  }

  /**
   * Format month as 3-letter abbreviation (e.g., "Jan", "Feb")
   */
  private formatMonth(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[date.getMonth()];
  }

  /**
   * Format weekday as 3-letter abbreviation (e.g., "Mon", "Tue")
   */
  private formatWeekday(date: Date): string {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  }
}
